import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import paymentsService from '../../services/paymentsService';

const KEY = ['payments', 'all'];

/**
 * Hook para buscar todas as formas de pagamento do usuário (globais + customizadas)
 * com cache longo (10 min) e invalidação manual apenas em mutations.
 * Reutilizável em múltiplos pontos do app sem refetches redundantes.
 *
 * @param {Object} options - Opções adicionais para customizar a query (ex: { enabled: boolean })
 */
export function usePaymentsAll(options = {}) {
  return useQuery({
    queryKey: KEY,
    queryFn: paymentsService.getAll,
    staleTime: 10 * 60 * 1000, // 10 min — invalidação é manual
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...options,
  });
}

/**
 * Hook que retorna uma função para invalidar o cache de pagamentos.
 * Use quando precisar limpar o cache manualmente fora do escopo de uma mutation.
 */
export function useInvalidatePayments() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['payments'] });
}

/**
 * Mutation para criar nova forma de pagamento customizada.
 * Invalida automaticamente o cache de pagamentos ao sucesso.
 */
export function useCreatePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: paymentsService.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payments'] }),
  });
}

/**
 * Mutation para atualizar forma de pagamento customizada.
 * Invalida automaticamente o cache de pagamentos ao sucesso.
 */
export function useUpdatePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => paymentsService.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payments'] }),
  });
}

/**
 * Mutation para deletar forma de pagamento customizada.
 * Invalida automaticamente o cache de pagamentos ao sucesso.
 */
export function useDeletePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => paymentsService.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payments'] }),
  });
}

// Exportar todas as funções juntas para conveniência
export default {
  usePaymentsAll,
  useInvalidatePayments,
  useCreatePayment,
  useUpdatePayment,
  useDeletePayment,
};
